const focusableSelector = [
    'a[href]',
    'area[href]',
    'button',
    'input:not([type="hidden"])',
    'select',
    'textarea',
    'iframe',
    'details > summary:first-of-type',
    '[tabindex]',
].join(',');

// checkVisibilityCSS is the older alias of visibilityProperty, the only one Chrome 105-120 understands. checkVisibility
// is missing from jsdom and Safari before 17.4, where the hidden attribute is the best remaining signal.
function isVisible(el: HTMLElement): boolean {
    const maybeSupported: Partial<Pick<HTMLElement, 'checkVisibility'>> = el;
    return maybeSupported.checkVisibility
        ? el.checkVisibility({ visibilityProperty: true, checkVisibilityCSS: true })
        : !el.closest('[hidden]');
}

function isCandidate(el: HTMLElement): boolean {
    return el.tabIndex >= 0 && !el.matches(':disabled') && !el.closest('[inert]') && isVisible(el);
}

// A radio group is a single tab stop: its checked radio, or its first radio when none is checked.
function isRadioTabStop(radio: HTMLInputElement, candidates: HTMLElement[]): boolean {
    if (!radio.name || radio.checked) return true;
    const group = candidates.filter(
        (el): el is HTMLInputElement =>
            el instanceof HTMLInputElement && el.type === 'radio' && el.name === radio.name && el.form === radio.form
    );
    return !group.some((r) => r.checked) && group[0] === radio;
}

/**
 * The common tab stops within `root`, in the order Tab visits them; `contenteditable` and media controls, which the panel
 * does not use, are not counted.
 */
export function tabbableElements(root: ParentNode): HTMLElement[] {
    // Matched element by element, as a selector list is not returned in document order by every engine (jsdom).
    const candidates = Array.from(root.querySelectorAll<HTMLElement>('*')).filter(
        (el) => el.matches(focusableSelector) && isCandidate(el)
    );
    const tabStops = candidates.filter(
        (el) => !(el instanceof HTMLInputElement && el.type === 'radio') || isRadioTabStop(el, candidates)
    );
    // Positive tabindex values come first, in ascending order; Array.prototype.sort is stable, so ties keep DOM order.
    const rank = (el: HTMLElement) => (el.tabIndex > 0 ? el.tabIndex : Number.MAX_SAFE_INTEGER);
    return tabStops.sort((a, b) => rank(a) - rank(b));
}

/** The elements Tab would visit after `after`, in order, skipping anything inside `exclude`. */
export function tabbablesAfter(after: HTMLElement, exclude?: Node | null): HTMLElement[] {
    const order = tabbableElements(after.ownerDocument.body).filter((el) => !exclude?.contains(el));
    const index = order.indexOf(after);
    return index >= 0
        ? order.slice(index + 1)
        : order.filter((el) => after.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING);
}
